// Financial Data Validator - Comprehensive data validation and version management
// Ensures data integrity and compatibility across different versions
// Last updated: January 2025

export class FinancialDataValidator {
    static _supportedVersions = ['1.0', '1.1', '1.2'];
    static _currentVersion = '1.2';
    static _versionSchemas = {
        '1.0': {
            requiredFields: ['contributionCaps', 'transferBalanceCap'],
            optionalFields: ['minimumPensionStandards', 'smsfFees']
        },
        '1.1': {
            requiredFields: ['contributionCaps', 'transferBalanceCap', 'minimumPensionStandards'],
            optionalFields: ['smsfFees', 'agePension']
        },
        '1.2': {
            requiredFields: ['contributionCaps', 'transferBalanceCap', 'minimumPensionStandards', 'smsfFees', 'agePension'],
            optionalFields: ['superTaxRates', 'workTest', 'bringForwardRule']
        }
    };

    /**
     * Validate financial data against schema
     * @param {Object} data - Data to validate
     * @param {string} version - Version to validate against
     * @returns {Object} Validation result
     */
    static validateData(data, version = this._currentVersion) {
        const result = {
            isValid: true,
            version: version,
            errors: [],
            warnings: [],
            compatibility: 'unknown',
            data: data
        };

        // Check if version is supported
        if (!this._supportedVersions.includes(version)) {
            result.isValid = false;
            result.errors.push(`Unsupported version: ${version}`);
            result.compatibility = 'incompatible';
            return result;
        }

        // Get schema for version
        const schema = this._versionSchemas[version];
        if (!schema) {
            result.isValid = false;
            result.errors.push(`No schema found for version: ${version}`);
            return result;
        }

        // Validate required fields
        schema.requiredFields.forEach(field => {
            if (!data.hasOwnProperty(field)) {
                result.isValid = false;
                result.errors.push(`Missing required field: ${field}`);
            } else {
                const fieldValidation = this._validateField(data[field], field, version);
                if (!fieldValidation.isValid) {
                    result.isValid = false;
                    result.errors.push(...fieldValidation.errors);
                }
                if (fieldValidation.warnings.length > 0) {
                    result.warnings.push(...fieldValidation.warnings);
                }
            }
        });

        // Validate optional fields if present
        schema.optionalFields.forEach(field => {
            if (data.hasOwnProperty(field)) {
                const fieldValidation = this._validateField(data[field], field, version);
                if (!fieldValidation.isValid) {
                    result.warnings.push(...fieldValidation.errors.map(err => `Optional field ${field}: ${err}`));
                }
                if (fieldValidation.warnings.length > 0) {
                    result.warnings.push(...fieldValidation.warnings.map(warn => `Optional field ${field}: ${warn}`));
                }
            }
        });

        // Determine compatibility
        result.compatibility = this._determineCompatibility(version);

        return result;
    }

    /**
     * Validate a specific field
     * @param {*} value - Value to validate
     * @param {string} fieldName - Name of the field
     * @param {string} version - Version being validated against
     * @returns {Object} Field validation result
     */
    static _validateField(value, fieldName, version) {
        const result = {
            isValid: true,
            errors: [],
            warnings: []
        };

        switch (fieldName) {
            case 'contributionCaps':
                this._validateContributionCaps(value, result);
                break;
            case 'transferBalanceCap':
                this._validateTransferBalanceCap(value, result);
                break;
            case 'minimumPensionStandards':
                this._validateMinimumPensionStandards(value, result);
                break;
            case 'smsfFees':
                this._validateSMSFFees(value, result);
                break;
            case 'agePension':
                this._validateAgePension(value, result);
                break;
            case 'superTaxRates':
                this._validateSuperTaxRates(value, result);
                break;
            case 'workTest':
                this._validateWorkTest(value, result);
                break;
            case 'bringForwardRule':
                this._validateBringForwardRule(value, result);
                break;
            default:
                result.warnings.push(`Unknown field: ${fieldName}`);
        }

        return result;
    }

    /**
     * Validate contribution caps
     * @param {Object} caps - Contribution caps data
     * @param {Object} result - Validation result object to update
     */
    static _validateContributionCaps(caps, result) {
        if (!caps || typeof caps !== 'object') {
            result.isValid = false;
            result.errors.push('Contribution caps must be an object');
            return;
        }

        const requiredFields = ['concessional', 'nonConcessional'];
        requiredFields.forEach(field => {
            if (!caps.hasOwnProperty(field)) {
                result.isValid = false;
                result.errors.push(`Missing ${field} in contribution caps`);
            } else if (typeof caps[field] !== 'number' || caps[field] <= 0) {
                result.isValid = false;
                result.errors.push(`Invalid ${field}: must be a positive number`);
            }
        });

        // Check for reasonable ranges
        if (caps.concessional && (caps.concessional < 10000 || caps.concessional > 100000)) {
            result.warnings.push(`Concessional cap outside typical range: $${caps.concessional}`);
        }

        if (caps.nonConcessional && (caps.nonConcessional < 50000 || caps.nonConcessional > 500000)) {
            result.warnings.push(`Non-concessional cap outside typical range: $${caps.nonConcessional}`);
        }
    }

    /**
     * Validate transfer balance cap
     * @param {number} cap - Transfer balance cap value
     * @param {Object} result - Validation result object to update
     */
    static _validateTransferBalanceCap(cap, result) {
        if (typeof cap !== 'number' || cap <= 0) {
            result.isValid = false;
            result.errors.push('Transfer balance cap must be a positive number');
        } else if (cap < 1000000 || cap > 5000000) {
            result.warnings.push(`Transfer balance cap outside typical range: $${cap}`);
        }
    }

    /**
     * Validate minimum pension standards
     * @param {Object} standards - Minimum pension standards data
     * @param {Object} result - Validation result object to update
     */
    static _validateMinimumPensionStandards(standards, result) {
        if (!standards || typeof standards !== 'object') {
            result.isValid = false;
            result.errors.push('Minimum pension standards must be an object');
            return;
        }

        if (!standards.withdrawalRates || typeof standards.withdrawalRates !== 'object') {
            result.isValid = false;
            result.errors.push('Missing or invalid withdrawalRates');
            return;
        }

        // Validate withdrawal rates for key ages
        const keyAges = [65, 70, 75, 80, 85, 90, 95];
        keyAges.forEach(age => {
            if (standards.withdrawalRates.hasOwnProperty(age)) {
                const rate = standards.withdrawalRates[age];
                if (typeof rate !== 'number' || rate <= 0 || rate > 0.2) {
                    result.isValid = false;
                    result.errors.push(`Invalid withdrawal rate for age ${age}: ${rate}`);
                }
            } else {
                result.warnings.push(`Missing withdrawal rate for age ${age}`);
            }
        });
    }

    /**
     * Validate SMSF fees
     * @param {Object} fees - SMSF fees data
     * @param {Object} result - Validation result object to update
     */
    static _validateSMSFFees(fees, result) {
        if (!fees || typeof fees !== 'object') {
            result.isValid = false;
            result.errors.push('SMSF fees must be an object');
            return;
        }

        const requiredSections = ['trusteeFees', 'auditCosts', 'complianceCosts'];
        requiredSections.forEach(section => {
            if (!fees.hasOwnProperty(section)) {
                result.isValid = false;
                result.errors.push(`Missing required SMSF fees section: ${section}`);
            }
        });

        // Validate trustee fees structure
        if (fees.trusteeFees) {
            if (!fees.trusteeFees.individual || !fees.trusteeFees.corporate) {
                result.isValid = false;
                result.errors.push('Trustee fees must include both individual and corporate structures');
            }
        }
    }

    /**
     * Validate age pension data
     * @param {Object} agePension - Age pension data
     * @param {Object} result - Validation result object to update
     */
    static _validateAgePension(agePension, result) {
        if (!agePension || typeof agePension !== 'object') {
            result.isValid = false;
            result.errors.push('Age pension data must be an object');
            return;
        }

        if (!agePension.rates || typeof agePension.rates !== 'object') {
            result.isValid = false;
            result.errors.push('Missing or invalid age pension rates');
        }

        if (!agePension.assetThresholds || typeof agePension.assetThresholds !== 'object') {
            result.isValid = false;
            result.errors.push('Missing or invalid asset thresholds');
        }
    }

    /**
     * Validate super tax rates
     * @param {Object} taxRates - Super tax rates data
     * @param {Object} result - Validation result object to update
     */
    static _validateSuperTaxRates(taxRates, result) {
        if (!taxRates || typeof taxRates !== 'object') {
            result.isValid = false;
            result.errors.push('Super tax rates must be an object');
            return;
        }

        const requiredRates = ['accumulationEarnings', 'pensionEarnings', 'cgtDiscountRate'];
        requiredRates.forEach(rate => {
            if (!taxRates.hasOwnProperty(rate)) {
                result.isValid = false;
                result.errors.push(`Missing required tax rate: ${rate}`);
            } else if (typeof taxRates[rate] !== 'number' || taxRates[rate] < 0 || taxRates[rate] > 1) {
                result.isValid = false;
                result.errors.push(`Invalid tax rate ${rate}: must be between 0 and 1`);
            }
        });
    }

    /**
     * Validate work test data
     * @param {Object} workTest - Work test data
     * @param {Object} result - Validation result object to update
     */
    static _validateWorkTest(workTest, result) {
        if (!workTest || typeof workTest !== 'object') {
            result.isValid = false;
            result.errors.push('Work test data must be an object');
            return;
        }

        if (typeof workTest.ageThreshold !== 'number' || workTest.ageThreshold < 60 || workTest.ageThreshold > 80) {
            result.isValid = false;
            result.errors.push('Invalid work test age threshold');
        }
    }

    /**
     * Validate bring forward rule data
     * @param {Object} bringForward - Bring forward rule data
     * @param {Object} result - Validation result object to update
     */
    static _validateBringForwardRule(bringForward, result) {
        if (!bringForward || typeof bringForward !== 'object') {
            result.isValid = false;
            result.errors.push('Bring forward rule data must be an object');
            return;
        }

        if (typeof bringForward.minimumContribution !== 'number' || bringForward.minimumContribution <= 0) {
            result.isValid = false;
            result.errors.push('Invalid minimum contribution for bring forward rule');
        }

        if (typeof bringForward.maximumContribution !== 'number' || bringForward.maximumContribution <= 0) {
            result.isValid = false;
            result.errors.push('Invalid maximum contribution for bring forward rule');
        }
    }

    /**
     * Determine compatibility level
     * @param {string} version - Version to check
     * @returns {string} Compatibility level
     */
    static _determineCompatibility(version) {
        const currentVersionNum = parseFloat(this._currentVersion);
        const checkVersionNum = parseFloat(version);

        if (checkVersionNum === currentVersionNum) {
            return 'current';
        } else if (checkVersionNum > currentVersionNum) {
            return 'future';
        } else if (checkVersionNum >= currentVersionNum - 0.1) {
            return 'compatible';
        } else {
            return 'legacy';
        }
    }

    /**
     * Migrate data from one version to another
     * @param {Object} data - Data to migrate
     * @param {string} fromVersion - Source version
     * @param {string} toVersion - Target version
     * @returns {Object} Migrated data
     */
    static migrateData(data, fromVersion, toVersion) {
        if (fromVersion === toVersion) {
            return data;
        }

        const migratedData = { ...data };

        // Migration from 1.0 to 1.1
        if (fromVersion === '1.0' && toVersion === '1.1') {
            if (!migratedData.minimumPensionStandards) {
                migratedData.minimumPensionStandards = {
                    withdrawalRates: {
                        65: 0.05, 70: 0.05, 75: 0.06, 80: 0.06, 85: 0.07, 90: 0.08, 95: 0.11
                    },
                    maximumWithdrawalRate: 0.10
                };
            }
        }

        // Migration from 1.1 to 1.2
        if (fromVersion === '1.1' && toVersion === '1.2') {
            if (!migratedData.smsfFees) {
                migratedData.smsfFees = {
                    trusteeFees: {
                        individual: { fixed: 500, percentage: 0.0025, maximum: 5000 },
                        corporate: { fixed: 800, percentage: 0.003, maximum: 8000 }
                    },
                    auditCosts: {
                        standard: 1200, complex: 2500, minimum: 600, maximum: 5000
                    },
                    complianceCosts: {
                        annualReturn: 400, bookkeeping: 800, administration: 600, softwareLicenses: 300
                    }
                };
            }

            if (!migratedData.agePension) {
                migratedData.agePension = {
                    rates: { single: 1000, couple: 1500 },
                    assetThresholds: { homeowner: 300000, nonHomeowner: 500000 }
                };
            }
        }

        return migratedData;
    }

    /**
     * Get supported versions
     * @returns {Array} Array of supported versions
     */
    static getSupportedVersions() {
        return [...this._supportedVersions];
    }

    /**
     * Get current version
     * @returns {string} Current version
     */
    static getCurrentVersion() {
        return this._currentVersion;
    }

    /**
     * Check if version is supported
     * @param {string} version - Version to check
     * @returns {boolean} True if supported
     */
    static isVersionSupported(version) {
        return this._supportedVersions.includes(version);
    }

    /**
     * Get version schema
     * @param {string} version - Version to get schema for
     * @returns {Object} Version schema
     */
    static getVersionSchema(version) {
        return this._versionSchemas[version] || null;
    }
}

// Export default instance
export default FinancialDataValidator;


